import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { useState } from "react";
import { SpacesTable } from "@/components/tables/spaces";
import { CreateSpaceDialog } from "@/dialogs/space-create-dialog";
import { DeleteSpaceDialog } from "@/dialogs/space-delete-dialog";
import { CreateGroupDialog } from "@/dialogs/group-create-dialog";
import { DeleteGroupDialog } from "@/dialogs/group-delete-dialog";
import { ReorderSpacesDrawer } from "@/dialogs/reorder-spaces-drawer";
import type { comunidadeContentQuery } from "./__generated__/comunidadeContentQuery.graphql";
import type {
	comunidade_groups$data,
	comunidade_groups$key,
} from "./__generated__/comunidade_groups.graphql";
import { DisabledScreenAlert } from "@/components/disabled-screen-alert";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/comunidade",
)({
	component: RouteComponent,
});

type SpaceDialogState = {
	groupId: string;
	groupName: string;
	spaceRef?: NonNullable<
		comunidade_groups$data["groups"]
	>[number]["spaces"][number];
} | null;

type GroupDialogState = {
	groupRef: NonNullable<comunidade_groups$data["groups"]>[number];
} | null;

type ReorderDrawerState = {
	open: boolean;
	initialTab: "grupos" | "feeds";
};

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const [spaceCreateDialog, setSpaceCreateDialog] =
		useState<SpaceDialogState>(null);
	const [spaceDeleteDialog, setSpaceDeleteDialog] =
		useState<SpaceDialogState>(null);
	const [groupEditDialog, setGroupEditDialog] =
		useState<GroupDialogState>(null);
	const [groupDeleteDialog, setGroupDeleteDialog] =
		useState<GroupDialogState>(null);
	const [reorderDrawer, setReorderDrawer] = useState<ReorderDrawerState>({
		open: false,
		initialTab: "grupos",
	});

	const data = useLazyLoadQuery<comunidadeContentQuery>(
		graphql`
			query comunidadeContentQuery($slug: String!) {
				club(slug: $slug) {
					id
					communityEnabled: configuration(key: "COMMUNITY_ENABLED")
					...spaceCreateDialog_clubFragment
					...comunidade_groups
					...reorderSpacesDrawer_club
				}
			}
		`,
		{ slug: club?.slug ?? "" },
		{ fetchPolicy: "store-and-network" },
	);
	const groupData = useFragment<comunidade_groups$key>(
		graphql`
    fragment comunidade_groups on Club {
      groups {
				id
				name
				position
				...groupCreateDialog_updateFragment
				...groupDeleteDialogFragment
				spaces {
					id
					name
					slug
					position
					visibility
					postPolicy
					commentPolicy
					...spaceCreateDialog_spaceFragment
					...spaceDeleteDialogFragment
				}
			}
    }
  `,
		data.club,
	);

	const groups =
		groupData?.groups.filter(Boolean).map((group) => ({
			id: group.id,
			name: group.name,
			spaces: group.spaces.filter(Boolean).map((space) => ({
				id: space.id,
				name: space.name,
				slug: space.slug,
				visibility: space.visibility,
				postPolicy: space.postPolicy,
				commentPolicy: space.commentPolicy,
				position: space.position || 1,
			})),
			position: group.position,
		})) ?? [];

	const findGroup = (groupId: string) =>
		groupData?.groups.filter(Boolean).find((g) => g.id === groupId);

	const findSpace = (spaceId: string) => {
		for (const group of groupData?.groups.filter(Boolean) ?? []) {
			const space = group.spaces.filter(Boolean).find((s) => s.id === spaceId);
			if (space) {
				return { space, group };
			}
		}
		return null;
	};

	const handleEditSpace = (spaceId: string) => {
		const result = findSpace(spaceId);
		if (result) {
			setSpaceCreateDialog({
				groupId: result.group.id,
				groupName: result.group.name,
				spaceRef: result.space,
			});
		}
	};

	const handleDeleteSpace = (spaceId: string) => {
		const result = findSpace(spaceId);
		if (result) {
			setSpaceDeleteDialog({
				groupId: result.group.id,
				groupName: result.group.name,
				spaceRef: result.space,
			});
		}
	};

	const handleCreateSpace = (groupId: string) => {
		const group = findGroup(groupId);
		if (group) {
			setSpaceCreateDialog({
				groupId: group.id,
				groupName: group.name,
			});
		}
	};

	const handleEditGroup = (groupId: string) => {
		const group = findGroup(groupId);
		if (group) {
			setGroupEditDialog({ groupRef: group });
		}
	};

	const handleDeleteGroup = (groupId: string) => {
		const group = findGroup(groupId);
		if (group) {
			setGroupDeleteDialog({ groupRef: group });
		}
	};

	const handleReorderGroups = () => {
		setReorderDrawer({ open: true, initialTab: "grupos" });
	};

	const handleReorderFeeds = () => {
		setReorderDrawer({
			open: true,
			initialTab: "feeds",
		});
	};

	const isDisabled = data.club.communityEnabled === "0";

	return (
		<div className="p-6 flex flex-col gap-6">
			{isDisabled && (
				<DisabledScreenAlert
					clubId={data.club.id}
					configKey="COMMUNITY_ENABLED"
					featureName="Comunidade"
				/>
			)}
			<SpacesTable
				groups={groups}
				isPending={false}
				onEditSpace={handleEditSpace}
				onDeleteSpace={handleDeleteSpace}
				onCreateSpace={handleCreateSpace}
				onEditGroup={handleEditGroup}
				onDeleteGroup={handleDeleteGroup}
				onReorderGroups={handleReorderGroups}
				onReorderFeeds={handleReorderFeeds}
			/>

			{data.club && spaceCreateDialog && (
				<CreateSpaceDialog
					open={!!spaceCreateDialog}
					onClose={() => setSpaceCreateDialog(null)}
					club={data.club}
					group={{
						id: spaceCreateDialog.groupId,
						name: spaceCreateDialog.groupName,
					}}
					space={spaceCreateDialog.spaceRef}
				/>
			)}

			{spaceDeleteDialog?.spaceRef && (
				<DeleteSpaceDialog
					open={!!spaceDeleteDialog}
					onClose={() => setSpaceDeleteDialog(null)}
					space={spaceDeleteDialog.spaceRef}
				/>
			)}

			{groupEditDialog?.groupRef && (
				<CreateGroupDialog
					open={!!groupEditDialog}
					onClose={() => setGroupEditDialog(null)}
					club={club.slug}
					group={groupEditDialog.groupRef}
				/>
			)}

			{groupDeleteDialog?.groupRef && (
				<DeleteGroupDialog
					open={!!groupDeleteDialog}
					onClose={() => setGroupDeleteDialog(null)}
					group={groupDeleteDialog.groupRef}
				/>
			)}

			{data.club && (
				<ReorderSpacesDrawer
					open={reorderDrawer.open}
					onOpenChange={(open) =>
						setReorderDrawer((prev) => ({ ...prev, open }))
					}
					club={data.club}
					initialTab={reorderDrawer.initialTab}
				/>
			)}
		</div>
	);
}
